import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ConfirmationDialogue, DropdownMenu, TransactionPaymentRail, TransactionType } from 'vopay';
import { Button } from 'UI';
import useFetchTransactionPaymentRails from '@/hooks/useApi/useFetchTransactionPaymentRails';
import useResubmitTransaction from '@/hooks/useApi/useResubmitTransaction';
import { useDataFetchIndicator } from '@/hooks';
import { useToast } from '@/providers/Toast.provider';

type RailDirection = 'funding' | 'withdrawal';

// FedNow has no TransactionType enum entry yet; the string matches the backend's type label
const RAIL_TRANSACTION_TYPES: { [rail in TransactionPaymentRail]: { [direction in RailDirection]?: string } } = {
  eft: { funding: TransactionType.EFT_FUNDING, withdrawal: TransactionType.EFT_WITHDRAWAL },
  ach: { funding: TransactionType.ACH_FUNDING, withdrawal: TransactionType.ACH_WITHDRAWAL },
  rtp: { withdrawal: TransactionType.RTP_WITHDRAWAL },
  fednow: { withdrawal: 'FedNow Withdrawal' },
  fedwire: { funding: TransactionType.FEDWIRE_FUNDING, withdrawal: TransactionType.FEDWIRE_WITHDRAWAL },
};

type ResubmitTransactionButtonProps = {
  transactionId: string;
  transactionType: string;
  currency: string;
  bearer: string;
};

export default function ResubmitTransactionButton({
  transactionId,
  transactionType,
  currency,
  bearer,
}: ResubmitTransactionButtonProps) {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.TransactionDetails.Resubmit',
  });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global.Buttons' });
  const history = useHistory();
  const toast = useToast();
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const { paymentRails, fetchTransactionPaymentRails } = useFetchTransactionPaymentRails();
  const { resubmitTransaction } = useResubmitTransaction();
  const [selectedRail, setSelectedRail] = useState<TransactionPaymentRail>();
  const [errorMessage, setErrorMessage] = useState('');

  const direction: RailDirection = transactionType.includes('Funding') ? 'funding' : 'withdrawal';
  const isSupportedType = Object.values(RAIL_TRANSACTION_TYPES).some((types) => types[direction] === transactionType);

  useEffect(() => {
    if (!isSupportedType) return;
    // Errors are already logged in the hook; the button simply stays hidden when rails can't be loaded
    fetchTransactionPaymentRails({ TransactionID: transactionId }, bearer).catch(() => {});
  }, [transactionId, bearer]);

  if (!paymentRails.length) return <></>;

  const railLabel = (rail: TransactionPaymentRail) =>
    t(`rails.${rail}.${direction}`, { defaultValue: RAIL_TRANSACTION_TYPES[rail][direction] ?? rail });

  const resubmit = async (rail: TransactionPaymentRail) => {
    setSelectedRail(undefined);
    try {
      setDataFetchingInfo(true, '', 'fullScreen');
      const res = await resubmitTransaction({ TransactionID: transactionId, PaymentRail: rail }, bearer);
      toast.success(t('toastSuccess', { defaultValue: 'Transaction resubmitted' }));
      // Replace instead of push so Back returns to the listing, not the resubmitted transaction
      history.replace({
        pathname: `/transactions-history/details/${(
          RAIL_TRANSACTION_TYPES[rail][direction] ?? transactionType
        ).replaceAll(' ', '')}/${res.TransactionID}/${currency}`,
        state: { transactionId: res.TransactionID },
      });
    } catch (error) {
      setErrorMessage((error as Error).message);
    } finally {
      setDataFetchingInfo(false);
    }
  };

  return (
    <>
      {paymentRails.length === 1 ? (
        <Button id="resubmit-transaction" onClick={() => setSelectedRail(paymentRails[0])}>
          {t('button', { defaultValue: 'Resubmit' })}
        </Button>
      ) : (
        <DropdownMenu>
          <DropdownMenu.Trigger asChild>
            <Button id="resubmit-transaction" iconRight="keyboard_arrow_down">
              {t('button', { defaultValue: 'Resubmit' })}
            </Button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Content align="end">
            {paymentRails.map((rail) => (
              <DropdownMenu.Item key={rail} onSelect={() => setSelectedRail(rail)}>
                {railLabel(rail)}
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu>
      )}

      <ConfirmationDialogue
        isVisible={!!selectedRail}
        status="alert"
        title={t('confirmDialogue.title', { defaultValue: 'Do you want to resubmit the transaction?' })}
        subTitle={
          selectedRail &&
          t('confirmDialogue.subTitle', {
            defaultValue: "You're about to resubmit this transaction using {{paymentRail}}",
            paymentRail: railLabel(selectedRail),
          })
        }
        confirm={{
          label: t('confirmDialogue.resubmit', { defaultValue: 'Resubmit' }),
          action: () => {
            if (selectedRail) void resubmit(selectedRail);
          },
        }}
        cancel={{
          label: tGlobal('cancel', { defaultValue: 'Cancel' }),
          action: () => setSelectedRail(undefined),
        }}
      />

      <ConfirmationDialogue
        isVisible={!!errorMessage}
        status="error"
        title={t('errorDialogue.title', { defaultValue: 'Error! The transaction was not resubmitted' })}
        subTitle={errorMessage}
        confirm={{
          label: tGlobal('back', { defaultValue: 'Back' }),
          action: () => setErrorMessage(''),
        }}
      />
    </>
  );
}
